import React from 'react';
import { ImagePlus, MapPin, MessageSquare, Milestone, Plus, RefreshCw, Search } from 'lucide-react';
import type { TravelCanvasSelection } from '../../../utils/peopleTravelCanvasGraph';
import type { PeopleTravelEvent, PeopleTravelLeg, PeopleTravelLocation } from '../../../types';
import '../../board/InspirationBoard.css';

type Props = {
  readOnly?: boolean;
  selection: TravelCanvasSelection | null;
  locations: PeopleTravelLocation[];
  events: PeopleTravelEvent[];
  legs: PeopleTravelLeg[];
  searchBusy?: boolean;
  plannerOpen?: boolean;
  onAddLocation: () => void;
  onAddEvent: () => void;
  onAddLeg: () => void;
  onAttachScreenshot: () => void;
  onSearchSelected: () => void;
  onRebuildChain: () => void;
  onTogglePlanner: () => void;
};

const PeopleTravelPlannerToolbar: React.FC<Props> = ({
  readOnly,
  selection,
  locations,
  events,
  legs,
  searchBusy,
  plannerOpen,
  onAddLocation,
  onAddEvent,
  onAddLeg,
  onAttachScreenshot,
  onSearchSelected,
  onRebuildChain,
  onTogglePlanner,
}) => {
  const canAddEvent = Boolean(selection?.locationId || selection?.eventId);
  const canSearch = Boolean(selection?.legId || selection?.eventId);
  const hasChain = locations.length > 0 || events.length > 0 || legs.length > 0;

  return (
    <div className="board-toolbar">
      <button type="button" className="board-tool" disabled={readOnly} onClick={onAddLocation}>
        <MapPin size={18} />
        <span>Location</span>
      </button>
      <button
        type="button"
        className="board-tool"
        disabled={readOnly || !canAddEvent}
        title={canAddEvent ? 'Add an event in the selected location' : 'Select a location first'}
        onClick={onAddEvent}
      >
        <Plus size={18} />
        <span>Event</span>
      </button>
      <button
        type="button"
        className="board-tool"
        disabled={readOnly || locations.length < 2}
        title={locations.length < 2 ? 'Add two locations first' : 'Connect the last two locations'}
        onClick={onAddLeg}
      >
        <Milestone size={18} />
        <span>Leg</span>
      </button>
      <button type="button" className="board-tool" disabled={readOnly} onClick={onAttachScreenshot}>
        <ImagePlus size={18} />
        <span>Screenshot</span>
      </button>
      <button
        type="button"
        className="board-tool"
        disabled={readOnly || !canSearch || searchBusy}
        title={canSearch ? 'Price the selected flight, drive, or hotel' : 'Select a travel leg or hotel card first'}
        onClick={onSearchSelected}
      >
        <Search size={18} />
        <span>{searchBusy ? 'Searching…' : 'Price selected'}</span>
      </button>
      <button
        type="button"
        className="board-tool"
        disabled={readOnly || !hasChain}
        onClick={onRebuildChain}
      >
        <RefreshCw size={18} />
        <span>Rebuild chain</span>
      </button>
      <span className="board-toolbar-sep" />
      <button
        type="button"
        className={`board-tool${plannerOpen ? ' is-active' : ''}`}
        onClick={onTogglePlanner}
      >
        <MessageSquare size={18} />
        <span>Planner</span>
      </button>
    </div>
  );
};

export default PeopleTravelPlannerToolbar;
